import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { useDispatch } from "react-redux";
import _, { last } from "lodash";

import Show from "../../../widgets/Show";
import Utils from "../../../../helpers/Utils";
import FormValue from "./form-components/FormValue";
import FormCanvas from "./form-components/FormCanvas";
import FormTextArea from "./form-components/FormTextArea";
import FormRadioList from "./form-components/FormRadioList";
import FormDatePicker from "./form-components/FormDatePicker";
import FormPatientDocs from "./form-components/FormPatientDocs";
import FormCheckboxList from "./form-components/FormCheckboxList";
import MedicalFillInFormOverlay from "./MedicalFillInFormOverlay";
import FormGenerateFormula from "./form-components/FormGenerateFormula";
import questionTypeCodes from "../../../../constants/chart-sidebar/questionTypeCodes";
import FormCheckboxListWithComment from "./form-components/FormCheckboxListWithComment";
import { DATE_FORMAT_LEGACY, FULL_DATE_FORMAT, TIME_FORMAT } from "../../../../constants/Constants";
import { consultationHistoryLogToggle } from "../../../../actions/chartSideMedicalFillInFormActions";
import medicalFillInFormItemTypes from "../../../../constants/chart-sidebar/medicalFillInFormItemTypes";

const NA = "N/A";

const MedicalFIllInFormViewItem = ({
  chartSideMedicalFillInForm,
  editMode,
  images,
  clinicName,
  consultation = null,
  onSaveItem,
}) => {
  const dispatch = useDispatch();
  const { stack, activeTab, empty, consultationResultHistory } = chartSideMedicalFillInForm;
  const [overlay, setOverlay] = useState({ visible: false });
  const [canvasImage, setCanvasImage] = useState("");
  const [patientImage, setPatientImage] = useState("");
  const loadedImagesRef = useRef(new Set());
  const [form, setForm] = useState(consultation);
  const lastStack = last(stack);

  const getTemplate = useCallback(
    (template = "") => {
      if (!template) return null;
      const reqExp = /\[\[[^\]]*]]/g;
      const { patient, member } = empty;
      return template.replace(reqExp, (match) => {
        const code = match.slice(2, -2);
        let pickListQuestion = false;
        template.replace(/PICKLIST/g, (m) => {
          if (m === "PICKLIST") pickListQuestion = true;
        });
        if (pickListQuestion) {
          const currentQuestion = (form.questions || []).find((item) => item.code === code);
          if (!currentQuestion || !currentQuestion?.options?.length) return "";
          const columns = Number(currentQuestion.typeCode.replace(/^\D+/g, "") || 0);
          let gridColumns = "auto ".repeat(columns);

          const pickListRootStyle = `
          text-decoration: none;
          display: inline-grid;
          gap: 10px;
          padding: 10px;
          grid-template-columns: ${gridColumns}`;

          let result = `<div style="${pickListRootStyle}">${currentQuestion?.options?.map(
            (option) => {
              return `<div><button data-index="${option.id}" id="${currentQuestion.id}-${
                option.id
              }" value="pick-list-${currentQuestion.id}" class="pick-list-button btn ${
                option.selected ? "btn-success" : "btn-default"
              }" style="padding: 1px">${option.title}</button>${
                option.comment
                  ? `<span style="display: block; font-size: 14px">Remarks</span>
                      <p
                        style="font-size: 14px"
                        class="form-control pick-list-comment"
                      >${option.comment || ""}</p>`
                  : ""
              }</div>`;
            },
          )}</div>`;
          return result.replaceAll(",", "");
        }
        if (code === "Current date") {
          return Utils.parseDateFormat(Utils.calcDateWithTimeZone(), DATE_FORMAT_LEGACY);
        }
        if (code === "Current time") {
          return Utils.parseDateFormat(Utils.calcDateWithTimeZone(), TIME_FORMAT);
        }
        if (code === "Patient chart number") {
          return patient && patient.chartNumber;
        }
        if (code === "Patient name") {
          return patient && patient.fullName;
        }
        if (code === "Patient nationality") {
          return _.get(patient, "nationality.name", "N/A");
        }
        if (code === "Patient primary insurance image") {
          for (const form in images) {
            for (const image in images[form]) {
              const { url } = images[form][image];

              if (url) {
                return `<img style="width: 100%; max-width: 300px" src=${url} alt="img"/>`;
              }
            }
          }
        }
        if (code === "Patient date of birth") {
          const dateFormat = localStorage.getItem("company-date-format");

          return Utils.parseDateFormat(
            Utils.calcDateWithTimeZone(patient && patient.dateOfBirth),
            dateFormat,
          );
        }
        if (code === "Dentist Name") {
          return member && member.name;
        }
        if (code === "Check-in date time") {
          return Utils.parseDateFormat(
            Utils.calcDateWithTimeZone(empty.checkedInDate),
            FULL_DATE_FORMAT,
          );
        }
        if (code === "Doctor license id") {
          return member && member.code;
        }
        if (code === "Patient last name") {
          return patient && patient.lastName;
        }
        if (code === "Patient first name") {
          return patient && patient.firstName;
        }
        if (code === "Patient primary card number") {
          return empty.primaryInsuranceCardNumber;
        }
        if (code === "Patient secondary card number") {
          return empty.secondaryInsuranceCardNumber;
        }
        if (code === "Patient gender") {
          return patient && patient.gender && patient.gender.name;
        }
        if (code === "Patient driver's licence number") {
          return patient && patient.documentDriverLicenseNumber;
        }
        if (code === "Patient social security number") {
          return patient && patient.documentSocialSecurityNumber;
        }
        if (code === "Patient social insurance number") {
          return patient && patient.documentSocialInsuranceNumber;
        }
        if (code === "Patient national id number") {
          return patient && patient.documentNationalIdNumber;
        }
        if (code === "Patient passport number") {
          return patient && patient.documentPasswordNumber;
        }
        if (code === "Clinic name") {
          return clinicName;
        }
        if (code === "Patient mobile number") {
          return patient && patient.mobilePhoneNumber;
        }
        return "N/A";
      });
    },
    [clinicName, empty, form.questions, images],
  );
  useEffect(() => {
    const handleLoadImage = (e) => {
      if (e.target.classList.contains("lazy-load-image-btn")) {
        const button = e.target;
        const encodedSrc = button.getAttribute("data-image-src");

        const dataIndex = button.getAttribute("data-image-index");
        const imgId = button.getAttribute("data-image-id");
        const imgClass = button.getAttribute("data-image-class");

        if (!encodedSrc) return;

        const imgSrc = atob(encodedSrc);

        button.textContent = "Loading...";
        button.disabled = true;

        const img = document.createElement("img");
        img.src = imgSrc;
        img.style.maxWidth = "100%";
        img.style.height = "auto";

        if (dataIndex) img.setAttribute("data-index", dataIndex);
        if (imgId) img.id = imgId;
        if (imgClass) img.className = imgClass + " loaded-dental-image";
        else img.className = "loaded-dental-image";

        img.onload = () => {
          const container = button.closest(".image-loader-container");
          if (container && container.parentNode) {
            container.parentNode.replaceChild(img, container);
            loadedImagesRef.current.add(imgSrc);
          }
        };

        img.onerror = () => {
          button.textContent = "Failed to load";
          button.disabled = false;
          button.classList.add("btn-danger");
        };
      }
    };

    document.addEventListener("click", handleLoadImage);
    return () => document.removeEventListener("click", handleLoadImage);
  }, []);

  useEffect(() => {
    setForm(consultation);
  }, [activeTab, consultation]);

  useEffect(() => {
    if (consultation.template) {
      consultation.template.templateBody = getTemplate(consultation.template?.templateBody);
      setForm(consultation);
    }
  }, [consultation, getTemplate]);

  useEffect(() => {
    if (consultation?.questions?.length) {
      setForm(consultation);
    }
  }, [consultation, consultation?.questions]);

  useEffect(() => {
    let isMounted = true;

    if (document.querySelector("span[data-index]")) {
      const elements = document.querySelectorAll("span[data-index]");
      elements.forEach((element) => {
        const question = form.questions.find((i) => Number(i.id) === Number(element.dataset.index));
        if (question) {
          if (question.typeCode === questionTypeCodes.CANVAS) {
            imageUrlToDataURL(
              question?.answerFile || question?.answerPhoto?.url || question?.questionPhoto?.url,
              (dataUrl) => {
                if (isMounted) {
                  setCanvasImage(dataUrl);
                }
              },
            );
            element.innerHTML = `${ReactDOMServer.renderToString(
              <img
                data-index={question.id}
                alt="canvas"
                id="canvasId"
                className="canvas-image"
                src={
                  question?.answerFile || question?.answerPhoto?.url || question?.questionPhoto?.url
                }
              />,
            )}`;
            return;
          }
          if (question.typeCode === questionTypeCodes.IMAGE) {
            imageUrlToDataURL(
              question?.answerFile || question?.answerPhoto?.url || question?.questionPhoto?.url,
              (dataUrl) => {
                if (isMounted) {
                  setPatientImage(dataUrl);
                }
              },
            );
            element.innerHTML = `${ReactDOMServer.renderToString(
              <img
                data-index={question.id}
                alt="mouth"
                id="mouthId"
                className="mouth-image"
                src={
                  question?.answerFile || question?.answerPhoto?.url || question?.questionPhoto?.url
                }
              />,
            )}`;
            return;
          }
          if (question.typeCode === questionTypeCodes.FORMULA) {
            element.innerHTML = Utils.calculateStringToNumber(question?.answer) || NA;
            return;
          }
          if (question?.options?.length) {
            if (question.typeCode.replace(/[^a-z]/gi, "") === questionTypeCodes.PICKLIST) return "";
            const { options } = question;
            const value = options
              .map((option) => {
                let answer = "";
                if (option.selected) {
                  answer = `${option.otherValue || (!option.other ? option.title : "")} <br>`;
                  if (option.comment) {
                    answer = answer + `${option.comment} <br>`;
                  }
                }
                return answer;
              })
              .join("");
            element.innerHTML = value || NA;
          } else {
            element.innerHTML = question?.answer || question?.defaultAnswer || NA;
          }
        }
      });
    }
    setTimeout(() => {
      const allImages = document.querySelectorAll(".medical-fill-in-form-question__body img");
      allImages.forEach((img) => {
        if (img.classList.contains("loaded-dental-image")) {
          return;
        }

        const imgSrc = img.src || img.getAttribute("src");
        if (imgSrc) {
          if (loadedImagesRef.current.has(imgSrc)) {
            return;
          }

          const dataIndex = img.getAttribute("data-index");
          const imgId = img.id;
          const imgClass = img.className;

          const buttonContainer = document.createElement("div");
          buttonContainer.className = "image-loader-container";
          buttonContainer.innerHTML = `
            <div style="
              background: white;
              border: 1px solid #e9ecef;
              border-radius: 10px;
              padding: 20px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
              transition: all 0.3s ease;
              text-decoration: unset;
            "
            onmouseover="this.style.boxShadow='0 4px 16px rgba(0,0,0,0.12)';"
            onmouseout="this.style.boxShadow='0 2px 8px rgba(0,0,0,0.08)';">
              <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
                <div style="
                  width: 40px;
                  height: 40px;
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                  border-radius: 8px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;
                ">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </div>
                <div style="flex: 1;">
                  <div style="font-size: 14px; font-weight: 600; color: #212529; margin-bottom: 2px;">Dental Image</div>
                  <div style="font-size: 12px; color: #6c757d;">Click to load and view</div>
                </div>
              </div>
              <button 
                class="btn btn-primary btn-sm btn-block lazy-load-image-btn" 
                data-image-src="${btoa(imgSrc)}"
                data-image-src="${btoa(imgSrc)}"
                data-image-index="${dataIndex || ""}"
                data-image-id="${imgId || ""}"
                data-image-class="${imgClass || ""}"
                style="
                  font-size: 13px;
                  padding: 10px;
                  border-radius: 6px;
                  font-weight: 500;
                  width: 100%;
                ">
                Load Image
              </button>
            </div>
      `;
          img.parentNode.replaceChild(buttonContainer, img);
        }
      });
    }, 0);

    return () => {
      isMounted = false;
    };
  }, [form.questions]);

  const getField = (questionTypeCode, questionId, visible, canAddComment = false) => {
    if (
      questionTypeCode === questionTypeCodes.TEXT ||
      questionTypeCode === questionTypeCodes.TEXT_AREA
    ) {
      return (
        <FormTextArea
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
        />
      );
    }
    if (questionTypeCode === questionTypeCodes.VALUE)
      return (
        <FormValue
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
        />
      );
    if (questionTypeCode === questionTypeCodes.DATE_PICKER)
      return (
        <FormDatePicker
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
        />
      );
    if (questionTypeCode === questionTypeCodes.RADIO_LIST)
      return (
        <FormRadioList
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
        />
      );
    if (questionTypeCode === questionTypeCodes.CHECKBOX_LIST && !canAddComment)
      return (
        <FormCheckboxList
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
        />
      );
    if (questionTypeCode === questionTypeCodes.CHECKBOX_LIST && canAddComment)
      return (
        <FormCheckboxListWithComment
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
        />
      );
    if (questionTypeCode === questionTypeCodes.CANVAS)
      return (
        <FormCanvas
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
          canvasImage={canvasImage}
        />
      );
    if (questionTypeCode === questionTypeCodes.FORMULA)
      return (
        <FormGenerateFormula
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
        />
      );
    if (questionTypeCode === questionTypeCodes.IMAGE)
      return (
        <FormPatientDocs
          setOverlay={setOverlay}
          visible={visible}
          editMode={editMode}
          questionId={questionId}
          setForm={setForm}
          form={form}
          patientImage={patientImage}
          questionTypeCode={questionTypeCode}
          questionTypeCodes={questionTypeCodes}
        />
      );
  };

  const setTemplateData = (event) => {
    if (!editMode) return;
    if (event.target.dataset?.index) {
      const id = event.target.dataset.index;
      const question = consultation.questions.find((item) => Number(item.id) === Number(id));
      setOverlay({
        visible: true,
        innerWidth: event.view.innerWidth,
        innerHeight: event.view.innerHeight,
        top: event.clientY,
        left: event.clientX,
        question: question.question || "",
        typeCode: question.typeCode,
        component: getField(question.typeCode, question.id, true, question.canAddComment),
      });
    }
  };

  const imageUrlToDataURL = (url, callback) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open("GET", url);
    xhr.responseType = "blob";
    xhr.send();
  };

  if (form.template) {
    const { template } = form;
    const templateBody = getTemplate(template?.templateBody);
    return (
      <div className="medical-fill-in-form-question">
        <MedicalFillInFormOverlay
          overlay={overlay}
          onClose={() => setOverlay({ visible: false })}
        />
        <div className="d-flex align-items-center justify-content-between mt-2 mb-2">
          <div className="medical-fill-in-form-question__title">{template.name}</div>
          <Show if={(consultationResultHistory || []).length}>
            <button
              className="btn btn-primary"
              onClick={() => dispatch(consultationHistoryLogToggle(true))}
            >
              View History
            </button>
          </Show>
        </div>
        <div
          className="medical-fill-in-form-question__body"
          dangerouslySetInnerHTML={{ __html: templateBody }}
          onClick={(event) => setTemplateData(event)}
        />
        <div className="medical-fill-in-form-question__actions">
          <Show if={editMode}>
            <button className="btn btn-primary" onClick={onSaveItem}>
              {lastStack?.type === medicalFillInFormItemTypes.new ? "Save" : "Update"}
            </button>
          </Show>
        </div>
      </div>
    );
  }
  return null;
};

export default MedicalFIllInFormViewItem;
